<template>
  <div v-if="!vm.loading" class="generate-catalog-selection-container">
    <div class="title">
      <div class="font-weight-bold mb-1">{{ $t("newCatalogName") }}</div>
      <div class="row">
        <div class="col-6">
          <b-form-input v-model="vm.localTargetCatalogName" id="targetCatalogNameInput" :class="{ 'border-danger': validationFeedbacks }"> </b-form-input>
        </div>
      </div>
    </div>
    <MenuBar withoutBorder>
      <template #leftItems>
        <div class="menubar-left-item-wrapper">
          <span class="font-weight-bold mb-1">{{ $t("Catalogs") }}</span>
          <span>{{ $t("catalogSelectionTitle") }}</span>
        </div>
      </template>
      <template #rightItems>
        <common-filter v-model="vm.filterText" :placeholder="$t('Filter')" />
      </template>
    </MenuBar>
    <div class="catalog-table-wrapper">
      <table class="table table-borderless catalog-table with-sticky-header">
        <thead class="header-border">
          <tr>
            <th class="name-column">{{ $t("name") }}</th>
            <th>{{ $t("Articles") }}</th>
            <th>{{ $t("VariantGroups") }}</th>
            <th>{{ $t("CatalogGroups") }}</th>
            <th>{{ $t("UpdatedOn") }}</th>
            <th>{{ $t("UpdatedBy") }}</th>
            <th>{{ $t("CreatedOn") }}</th>
            <th>{{ $t("CreatedBy") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            :key="catalog[primaryLanguageCode].catalogId"
            v-for="catalog in filteredCatalogs"
            @click="vm.localSourceCatalogId = catalog[primaryLanguageCode]?.catalogId"
            :class="{ selected: vm.localSourceCatalogId === catalog[primaryLanguageCode].catalogId }">
            <td
              :class="{
                'selection-indicator-box': vm.localSourceCatalogId === catalog[primaryLanguageCode].catalogId,
                'non-selection-indicator-box': vm.localSourceCatalogId !== catalog[primaryLanguageCode].catalogId
              }">
              {{ catalog[primaryLanguageCode].name }}
            </td>
            <td>{{ catalog[primaryLanguageCode].catalogArticles }}</td>
            <td>{{ catalog[primaryLanguageCode].variationGroups }}</td>
            <td>{{ catalog[primaryLanguageCode].catalogGroups }}</td>
            <td>{{ $d(catalog[primaryLanguageCode].updatedOn, "shortDateTime") }}</td>
            <td>{{ catalog[primaryLanguageCode].updatedBy }}</td>
            <td>{{ $d(catalog[primaryLanguageCode].createdOn, "shortDateTime") }}</td>
            <td>{{ catalog[primaryLanguageCode].createdBy }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else class="spinner-container">
    <Spinner />
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ChannelCreatorState } from "@medapro-types/ChannelCreator/store/channel-creator-store";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import MenuBar from "@common/components/MenuBar/MenuBar.vue";
import commonFilter from "@common/components/CommonFilter.vue";
import _ from "lodash";
import { useContentLanguages } from "@common/composable/useMetaDataStore";

//#region General
const props = defineProps<{ sourceCatalogId: string; targetCatalogName: string }>();
const emits = defineEmits(["update:sourceCatalogId", "update:targetCatalogName"]);
const { t: $t, d: $d } = useI18n();
//#endregion

//#region Store
const { primaryLanguageCode } = useContentLanguages();

const { useState: useChannelCreatorState } = createNamespacedHelpers<ChannelCreatorState>("channelCreator");
const { catalogs } = useChannelCreatorState(["catalogs"]);
//#endregion

//#region ViewModel

type ViewModel = {
  loading: boolean;
  localTargetCatalogName: string | null;
  localSourceCatalogId: string | null;
  filterText: string;
};

const vm = reactive<ViewModel>({
  loading: false,
  localTargetCatalogName: "",
  localSourceCatalogId: "",
  filterText: ""
});

const state = reactive({
  isValid: true
});
//#endregion

//#region validation
const validationFeedbacks = computed(() => {
  const isNameEmpty = vm.localTargetCatalogName == "" || vm.localTargetCatalogName == null;
  const isDuplicateNameExists =
    vm.localTargetCatalogName &&
    _.filter(
      catalogs.value,
      (x) =>
        x &&
        x[primaryLanguageCode.value] &&
        x[primaryLanguageCode.value].name &&
        x[primaryLanguageCode.value]?.name.toLowerCase() == vm.localTargetCatalogName?.toLowerCase()
    ).length > 0;
  const isNameLengthExceeds = (vm.localTargetCatalogName ?? "").length > 256;

  if (isNameEmpty) return $t("validation.valueRequired", { field: $t("name") });
  else if (isDuplicateNameExists)
    return $t("validation.duplicateExists", {
      field: $t("name")
    });
  else if (isNameLengthExceeds)
    return $t("validation.lengthExceeds", {
      field: $t("name", 1),
      length: 128
    });
  else return null;
});
//#endregion

const filteredCatalogs = computed(() =>
  !vm.filterText
    ? catalogs.value
    : _.filter(
        catalogs.value,
        (x) => x && x[primaryLanguageCode.value]?.name && x[primaryLanguageCode.value]?.name.toLowerCase().indexOf(vm.filterText.toLowerCase()) >= 0
      )
);

//#region LifeCycle
onMounted(() => {
  vm.localTargetCatalogName = props.targetCatalogName;
  vm.localSourceCatalogId = props.sourceCatalogId;
  vm.filterText = "";
});

//#endregion

//#region watch
watch(
  () => vm.localSourceCatalogId,
  (nv) => {
    emits("update:sourceCatalogId", nv);
  },
  { deep: true }
);
watch(
  () => vm.localTargetCatalogName,
  (nv) => {
    emits("update:targetCatalogName", nv);
  },
  { deep: true }
);

watch(
  () => validationFeedbacks.value,
  (nv) => {
    if (nv) {
      state.isValid = false;
    } else {
      state.isValid = true;
    }
  },
  { deep: true }
);
//#endregion

//#region Exposed

defineExpose({ state });

//#endregion
</script>
